import { io, Socket } from 'socket.io-client';
import { ECHATMETHOD } from '../../../shared/core-types/chat.enum';
import { Eventbus } from './eventbus';
import { Logger } from './logger';

/**
 * Defines the structure of an online solo request response.
 *
 * @remarks
 * Returned by {@link ChatSocket.sendOnlineSoloRequest}. `response` is transport-opaque
 * and depends on the server endpoint being invoked.
 */
export interface IsendOnlineSoloRequestRes {
  success: boolean;
  response?: unknown;
  error?: string;
}

/** Default timeout for socket requests */
const requestTimeout = 10000;

/**
 * Union of all incoming notification handler method names.
 *
 * @remarks
 * These names must match both the decorator registration and the method keys on
 * {@link ChatSocket}. Adding a new handler requires updating this union.
 */
type TreqMethod = 'roomCreated' | 'chatMessage' | 'deleteMessage' | 'newPeer' | 'newMainPeer' | 'peerClosed' | 'mainPeerClosed' | 'updateStatus' | 'updatePeer' | 'updateRoom' | 'updateRoomOnNew' | 'peerOffline' | 'peerOnline' | 'updateViewCount';

/**
 * Map that stores chat notifications.
 * The key is the notification ID and the value is the notification method name.
 */
const chatNotificationMap = new Map<string, TreqMethod>();

/**
 * Decorator that registers a method as a chat notification handler.
 *
 * @param ref - Optional explicit notification key. When omitted, the method name is used.
 * @returns The original property descriptor for chaining.
 *
 * @example
 * // Registers `chatMessage` so it can be invoked by setupNotificationHandler()
 * @chatNotification()
 * private chatMessage(data) {  }
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const chatNotification = (ref = '') => (target: any, propertyKey: TreqMethod, descriptor: PropertyDescriptor) => {
  try {
    if (ref) {
      chatNotificationMap.set(ref, propertyKey);
    } else {
      chatNotificationMap.set(propertyKey, propertyKey);
    }

    return descriptor;
  } catch (error) {
    // Silently log potential issues without breaking the application
    console.error('Error in chat notification registration:', error);

    return descriptor;
  }
};

/**
 * Manages WebSocket communication for chat functionality.
 *
 * @remarks
 * - Establishes and maintains a socket.io connection.
 * - Translates incoming notifications to {@link ECHATMETHOD} events on an {@link Eventbus}.
 * - Provides request helpers with timeout handling.
 *
 * @example
 * const socket = new ChatSocket(BASE_URL, userId, getToken);
 * await socket.connect();
 * const ok = socket.isSocketConnected();
 */
export class ChatSocket {
  /** Current socket connection mode */
  static mode: string;

  /** Event bus for chat-related communications */
  eventbus: Eventbus;

  /** Logging utility */
  private logger: Logger;

  /** Active socket connection */
  private socket?: Socket;

  /**
   * Initializes a new chat socket connection.
   *
   * @param url - WebSocket server base URL.
   * @param id - Unique connection identifier (user id).
   * @param getAuthToken - Async provider returning an auth token appended as a query param.
   */
  constructor(
    private url: string,
    private id: string,
    private getAuthToken: () => Promise<string>
  ) {
    this.eventbus = new Eventbus();
    this.logger = new Logger();
    this.connect();
  }

  /**
   * Retrieves the current active socket instance.
   *
   * @returns The underlying socket.io {@link Socket} or `undefined` if not connected.
   */
  get currSocket() {
    return this.socket;
  }

  /**
   * Handles the event when a room is created.
   *
   * @param data - Server-sent payload for the created room notification.
   */
  @chatNotification()
  private roomCreated(data) {
    this.eventbus.chat$.next({
      type: ECHATMETHOD.ROOM_CREATED,
      data
    });
  }

  /**
   * Handles an incoming chat message notification.
   *
   * @param data - Message payload from the server.
   */
  @chatNotification()
  private chatMessage(data) {
    this.eventbus.chat$.next({
      type: ECHATMETHOD.CHAT_MESSAGE,
      data
    });
  }

  /**
   * Handles message deletion notification.
   *
   * @param data - Deletion payload from the server.
   */
  @chatNotification()
  private deleteMessage(data) {
    this.eventbus.chat$.next({
      type: ECHATMETHOD.DELETE_MESSAGE,
      data
    });
  }

  /**
   * Handles new peer joined notification.
   *
   * @param data - Peer payload from the server.
   */
  @chatNotification()
  private newPeer(data) {
    this.eventbus.chat$.next({
      type: ECHATMETHOD.NEW_PEER,
      data
    });
  }

  /**
   * Handles new main peer notification.
   *
   * @param data - Main peer payload from the server.
   */
  @chatNotification()
  private newMainPeer(data) {
    this.eventbus.chat$.next({
      type: ECHATMETHOD.NEW_MAIN_PEER,
      data
    });
  }

  /**
   * Handles peer closed notification.
   *
   * @param data - Payload including peer identification.
   */
  @chatNotification()
  private peerClosed(data: unknown): void {
    this.eventbus.chat$.next({
      type: ECHATMETHOD.PEER_CLOSE,
      data
    });
  }

  /**
   * Handles main peer closed notification.
   *
   * @param data - Payload including peer identification.
   */
  @chatNotification()
  private mainPeerClosed(data) {
    this.eventbus.chat$.next({
      type: ECHATMETHOD.MAIN_PEER_CLOSE,
      data
    });
  }

  /**
   * Handles status update notification.
   *
   * @param data - Payload including status update details.
   */
  @chatNotification()
  private updateStatus(data) {
    this.eventbus.chat$.next({
      type: ECHATMETHOD.UPDATE_STATUS,
      data
    });
  }

  /**
   * Handles peer update notification.
   *
   * @param data - Payload including updated peer information.
   */
  @chatNotification()
  private updatePeer(data: unknown): void {
    this.eventbus.chat$.next({
      type: ECHATMETHOD.PEER_UPDATE,
      data
    });
  }

  /**
   * Handles room update notification.
   *
   * @param data - Payload including room updates.
   */
  @chatNotification()
  private updateRoom(data) {
    this.eventbus.chat$.next({
      type: ECHATMETHOD.UPDATE_ROOM,
      data
    });
  }

  /**
   * Handles peer went offline notification.
   *
   * @param data - Payload including peer identification.
   */
  @chatNotification()
  private peerOffline(data) {
    this.eventbus.chat$.next({
      type: ECHATMETHOD.PEER_OFFLINE,
      data
    });
  }

  /**
   * Handles peer came online notification.
   *
   * @param data - Payload including peer identification.
   */
  @chatNotification()
  private peerOnline(data) {
    this.eventbus.chat$.next({
      type: ECHATMETHOD.PEER_ONLINE,
      data
    });
  }

  /**
   * Handles view count update notification.
   *
   * @param data - Payload including view count information.
   */
  @chatNotification()
  private updateViewCount(data) {
    this.eventbus.chat$.next({
      type: ECHATMETHOD.UPDATE_VIEW_COUNT,
      data
    });
  }

  /**
   * Checks if the socket is connected.
   *
   * @returns True if the socket is currently connected; otherwise false.
   */
  isSocketConnected() {
    return Boolean(this.socket?.connected);
  }

  /**
   * Disconnects the socket connection if present.
   */
  disconnectSocket() {
    this.socket?.disconnect();
  }

  /**
   * Connects to the WebSocket server.
   *
   * @param id - The user ID to associate with the connection. Defaults to the constructor `id`.
   * @param url - The WebSocket server base URL. Defaults to the constructor `url`.
   *
   * @remarks
   * Appends `authToken` and `userId` as query parameters when establishing the connection.
   */
  async connect(
    id = this.id,
    url = this.url
  ) {
    const authToken = await this.getAuthToken();
    const socketUrl = `${url}/?authToken=${authToken}&userId=${id}`;

    this.logger.debug('Socket:connect:: - socketUrl :', socketUrl);

    this.socket = io(socketUrl, {
      timeout: 3000,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelayMax: 2000,
      transports: ['websocket']
    });
    this.setupEventHandler(this.socket);
    this.setupNotificationHandler();
  }

  /**
   * Sends a request to the server using the WebSocket connection.
   *
   * @param roomId - The target room id (used to namespace the request channel).
   * @param method - The method name of the request.
   * @param data - Optional payload for the request.
   * @param timeOut - Timeout in milliseconds (default: 10000).
   * @returns A promise that resolves with the response from the server.
   * @throws If there is no socket connection or if the request times out.
   */
  sendRequest(roomId: string, method, data?, timeOut = 10000) {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.socket.connected) {
        this.logger.error('Socket:connect:: -', 'No socket connection');
        reject('No socket connection');
      } else {
        this.socket.emit('mainrequest' + roomId, { method, data }, this.timeoutCallback((err, response) => {
          if (err) {
            this.logger.error(
              'sendRequest %s timeout! socket: %o',
              method,
              this.socket
            );
            reject(err);
          } else {
            this.logger.debug('sendRequest response: %s', response);
            resolve(response);
          }
        }, timeOut));
      }
    });
  }

  /**
   * Sends an online (non-room) request to the server.
   *
   * @param method - The method of the request.
   * @param data - Optional payload for the request.
   * @param timeOut - Timeout in milliseconds (default: 10000).
   * @returns A promise that resolves with the response from the server.
   * @throws If there is no socket connection or if the request times out.
   */
  sendOnlineSoloRequest(method, data?, timeOut = 10000) {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.socket.connected) {
        this.logger.error('Socket:sendOnlineSoloRequest:: - connect -', 'No socket connection');
        reject('No socket connection');
      } else {
        this.socket.emit('onlinerequest', { method, data }, this.timeoutCallback((err, response) => {
          if (err) {
            this.logger.error(
              'sendOnlineSoloRequest::sendRequest %s timeout! socket: %o',
              method,
              this.socket
            );
            reject(err);
          } else {
            resolve(response);
          }
        }, timeOut));
      }
    });
  }

  /**
   * Wraps a callback with timeout handling.
   *
   * @param callback - Node-style callback `(err, response) => void` supplied to socket.emit.
   * @param timeOut - Timeout duration in milliseconds. Default is 10000.
   * @returns A wrapper function that enforces a single invocation and times out if not called.
   */
  timeoutCallback(callback, timeOut = 10000) {
    let called = false;

    const interval = setTimeout(() => {
      if (called) {
        return;
      }
      called = true;
      this.logger.error('Socket:connect:: -', 'nowRequest timeout');
      callback(new Error('nowRequest timeout'));
    }, timeOut || requestTimeout);

    return (...args) => {
      if (called) {
        return;
      }
      called = true;
      clearTimeout(interval);

      callback(...args);
    };
  }

  /**
   * Disconnects the WebSocket connection.
   *
   * @remarks
   * Safe to call repeatedly; no-ops when already disconnected.
   */
  disconnect() {
    if (this.socket?.disconnected) {
      this.logger.debug('Socket:disconnect:: - socket already disconnected');

      return;
    }
    this.socket?.disconnect();
  }

  /**
   * Sets up socket lifecycle event handlers (connect/reconnect/disconnect/etc.).
   *
   * @param socket - The socket instance to attach handlers to.
   */
  private setupEventHandler(socket: Socket) {
    socket.on('connect', () => {
      this.logger.debug('Socket:setupEventHandler:: - socket connected !');
      this.eventbus.chat$.next({
        type: ECHATMETHOD.SOCKET_CONNECTED
      });
    });

    socket.on('connect_error', () => {
      this.logger.warn('Socket:setupEventHandler:: - reconnect_failed !');
    });

    socket.on('connect_timeout', () => {
      this.logger.warn('Socket:setupEventHandler:: - connect_timeout !');
    });

    socket.on('disconnect', (reason) => {
      this.logger.error(
        'Socket:setupEventHandler:: - Socket disconnect, reason: %s',
        reason
      );
      this.eventbus.chat$.next({
        type: ECHATMETHOD.SOCKET_DISCONNECTED
      });

      if (reason === 'io server disconnect') {
        this.connect();
      }

      /* if (reason === 'io server disconnect' || reason === 'transport close' || reason === 'ping timeout' || reason === 'transport error') {
        this.connect();
      } */
    });

    socket.on('reconnect', attemptNumber => {
      this.logger.debug('Socket:setupEventHandler:: - "reconnect" event [attempts:"%s"]', attemptNumber);
    });

    socket.on('reconnect_failed', () => {
      this.logger.warn('Socket:setupEventHandler:: - reconnect_failed !');
    });
  }

  /**
   * Sets up the notification handler for incoming server events.
   *
   * @remarks
   * Listens for the `mainnotification` event and dispatches to registered handlers
   * via the decorator-based registry.
   */
  private setupNotificationHandler() {
    const socket = this.socket;

    socket?.on('mainnotification', (request) => {
      this.logger.debug('Socket:setupNotificationHandler:: - mainnotification event, method: %s,data: %o', request.method, request.data);

      const regiMethod: TreqMethod | undefined = chatNotificationMap.get(request.method);

      if (!regiMethod) {
        this.logger.warn('Socket:setupNotificationHandler:: - mainnotification method: %s, do not register!', request.method);

        return;
      }

      this[regiMethod](request.data);
    });
  }
}
